import {
  BMBadgeTag,
  BMComponentsType,
  BMElevationType,
  BMPrimitives,
  BMRadioButton,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import { useMemo } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import BMView from "../../View/BMView";
import { appendTestId } from "../../../utils";
import type { CorporateOption, BMCorporateSelectionPopupVariant } from "../types";

const SIGNIFIER_RADIO_SIZE = 20;

type Props = {
  corporate: CorporateOption;
  selected: boolean;
  variant: BMCorporateSelectionPopupVariant;
  disabled?: boolean;
  activeBadgeLabel?: string;
  onPress: () => void;
  testID?: string;
};

export default function CorporateOptionCard({
  corporate,
  selected,
  variant,
  disabled,
  activeBadgeLabel = "Active",
  onPress,
  testID,
}: Props) {
  const { BMComponents, BMElevation } = useBMTheme();
  const styles = useMemo(
    () => createStyles(BMComponents, BMElevation),
    [BMComponents, BMElevation]
  );
  const isMobile = variant === "mobile";
  const isDisabled = disabled || corporate.isActive;

  const handleKeyDown = (e: { key: string; preventDefault: () => void }) => {
    if (isDisabled) return;
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      onPress();
    }
  };

  return (
    <Pressable
      onPress={onPress}
      disabled={isDisabled}
      testID={appendTestId(testID, `option-${corporate.id}`)}
      style={({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) => [
        styles.card,
        isMobile ? styles.cardMobile : styles.cardDesktop,
        hovered && !isDisabled && !selected && styles.cardHovered,
        selected && !isDisabled && !isMobile && styles.cardDesktopSelected,
        isDisabled && styles.cardDisabled,
        pressed && !isDisabled && styles.cardPressed,
      ]}
      accessibilityRole="radio"
      accessibilityState={{ selected, disabled: isDisabled }}
      {...({ onKeyDown: handleKeyDown } as object)}
    >
      <BMView row align="center" justify="between" flex>
        <View style={styles.leftSection}>
          <View pointerEvents="none">
            {corporate.isActive ? (
              <View style={styles.signifierRadio} />
            ) : (
              <BMRadioButton value={selected} disabled={isDisabled} />
            )}
          </View>
          <View style={styles.textColumn}>
            <BMText
              variant="uiLarge"
              numberOfLines={1}
              ellipsizeMode="tail"
              ink={
                isDisabled
                  ? "BMStyleTextInkMuted"
                  : "BMStyleTextInkPrimary"
              }
            >
              {corporate.alias}
            </BMText>
            {corporate.name ? (
              <BMText
                variant="uiNormal"
                numberOfLines={isMobile ? 2 : 1}
                ellipsizeMode="tail"
                ink={
                  isDisabled
                    ? "BMStyleTextInkMuted"
                    : "BMStyleTextInkSecondary"
                }
              >
                {corporate.name}
              </BMText>
            ) : null}
          </View>
        </View>
        {corporate.isActive && (
          <View style={styles.badgeWrapper}>
            <BMBadgeTag
              label={activeBadgeLabel}
              type="GREEN"
              isDesktop={!isMobile}
              testID={appendTestId(testID, "active-badge")}
            />
          </View>
        )}
      </BMView>
    </Pressable>
  );
}

const createStyles = (
  BMComponents: BMComponentsType,
  BMElevation: BMElevationType
) =>
  StyleSheet.create({
    card: {
      borderRadius: BMPrimitives.BMRadius.md,
    },
    cardDesktop: {
      backgroundColor: BMComponents.BMStyleSurfaceBgcolor,
      padding: BMPrimitives.BMSize.lg,
      width: 552,
      ...BMElevation.elevation2,
    },
    cardMobile: {
      backgroundColor: BMComponents.BMStyleSurfaceBgcolor,
      padding: BMPrimitives.BMSize.lg,
      alignSelf: "stretch",
      minHeight: 60,
      ...BMElevation.elevation2,
    },
    cardDesktopSelected: {
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleLineBordercolorActive,
    },
    cardHovered: {
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorActive,
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleLineBordercolorBold,
    },
    cardPressed: {
      opacity: 0.85,
    },
    cardDisabled: {
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorDisabled,
    },
    signifierRadio: {
      width: SIGNIFIER_RADIO_SIZE,
      height: SIGNIFIER_RADIO_SIZE,
      borderRadius: BMPrimitives.BMRadius.rounded,
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleIconInkMuted,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorDisabled,
    },
    badgeWrapper: {
      alignSelf: "center",
    },
    leftSection: {
      flexDirection: "row",
      alignItems: "center",
      gap: BMPrimitives.BMSize.sm,
      flex: 1,
      minWidth: 0,
    },
    textColumn: {
      flex: 1,
      minWidth: 0,
    },
  });
